import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { FlightsFareTypes, FlightsClassTypes } from 'constants/flight'
import type { FlightDropdownOption } from 'components/Flights/types'
import config from 'constants/config'
import { getSkyLoungeLabel } from 'lib/flights/sharedFlightLabels'

export function getFareTypeLabel(intl: IntlShape, fareType: FlightsFareTypes | `${FlightsFareTypes}`): string {
  switch (fareType as FlightsFareTypes) {
    case FlightsFareTypes.RETURN:
      return intl.formatMessage({
        id: 'cB_GyMx0uCxX4cEOfVLn',
        defaultMessage: 'Return',
      })
    case FlightsFareTypes.ONE_WAY:
      return intl.formatMessage({
        id: 'VyqJ8Ko9-KQP7yiVsGtl',
        defaultMessage: 'One-way',
      })
    case FlightsFareTypes.MULTI_CITY:
      return intl.formatMessage({
        id: 'JXUghEdPFaB0501nRSI7',
        defaultMessage: 'Multi-city',
      })
  }
}

export function getFareCabinLabel(intl: IntlShape, classType: FlightsClassTypes): string {
  switch (classType) {
    case FlightsClassTypes.ECONOMY:
      return intl.formatMessage({
        id: 'sjBhVyeefjN8xsn2JKz6',
        defaultMessage: 'Economy',
      })
    case FlightsClassTypes.PREMIUM:
      return intl.formatMessage({
        id: 'ckVGbMoSjV9_RMqv51wM',
        defaultMessage: 'Premium Economy',
      })
    case FlightsClassTypes.BUSINESS:
      return intl.formatMessage({
        id: 'm1rEYIqGvFXJROBQkdJU',
        defaultMessage: 'Business',
      })
    case FlightsClassTypes.FIRST:
      return intl.formatMessage({
        id: 'gttp5pKtDl2icrYk1l18',
        defaultMessage: 'First',
      })
    case FlightsClassTypes.LUXROW:
    case FlightsClassTypes.LUXROW_PAX_ONE:
      return getSkyLoungeLabel(intl)
  }
}

function useFlightFareLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const fareTypeLabelMap: Record<FlightsFareTypes, string> = {
      [FlightsFareTypes.RETURN]: getFareTypeLabel(intl, FlightsFareTypes.RETURN),
      [FlightsFareTypes.ONE_WAY]: getFareTypeLabel(intl, FlightsFareTypes.ONE_WAY),
      [FlightsFareTypes.MULTI_CITY]: getFareTypeLabel(intl, FlightsFareTypes.MULTI_CITY),
    }

    const fareCabinLabelMap: Record<FlightsClassTypes, string> = {
      [FlightsClassTypes.ECONOMY]: getFareCabinLabel(intl, FlightsClassTypes.ECONOMY),
      [FlightsClassTypes.PREMIUM]: getFareCabinLabel(intl, FlightsClassTypes.PREMIUM),
      [FlightsClassTypes.BUSINESS]: getFareCabinLabel(intl, FlightsClassTypes.BUSINESS),
      [FlightsClassTypes.FIRST]: getFareCabinLabel(intl, FlightsClassTypes.FIRST),
      [FlightsClassTypes.LUXROW]: getFareCabinLabel(intl, FlightsClassTypes.LUXROW),
      [FlightsClassTypes.LUXROW_PAX_ONE]: getFareCabinLabel(intl, FlightsClassTypes.LUXROW_PAX_ONE),
    }

    const fareTypes: Array<FlightDropdownOption> = [
      {
        value: FlightsFareTypes.RETURN,
        label: fareTypeLabelMap[FlightsFareTypes.RETURN],
        disabled: false,
      },
      {
        value: FlightsFareTypes.ONE_WAY,
        label: fareTypeLabelMap[FlightsFareTypes.ONE_WAY],
        disabled: !config.ENABLE_ONE_WAY_FLIGHTS,
        comingSoon: !config.ENABLE_ONE_WAY_FLIGHTS,
      },
      {
        value: FlightsFareTypes.MULTI_CITY,
        label: fareTypeLabelMap[FlightsFareTypes.MULTI_CITY],
        disabled: false,
        comingSoon: false,
        isNew: true,
      },
    ]

    const fareCabins: Array<FlightDropdownOption<FlightsClassTypes>> = [
      {
        value: FlightsClassTypes.ECONOMY,
        label: fareCabinLabelMap[FlightsClassTypes.ECONOMY],
        disabled: false,
      },
      {
        value: FlightsClassTypes.PREMIUM,
        label: fareCabinLabelMap[FlightsClassTypes.PREMIUM],
        disabled: false,
      },
      {
        value: FlightsClassTypes.BUSINESS,
        label: fareCabinLabelMap[FlightsClassTypes.BUSINESS],
        disabled: false,
      },
      {
        value: FlightsClassTypes.FIRST,
        label: fareCabinLabelMap[FlightsClassTypes.FIRST],
        disabled: false,
      },
    ]

    return {
      fareTypeLabelMap,
      fareCabinLabelMap,
      fareTypes,
      fareCabins,
    }
  }, [intl])
}

export default useFlightFareLabels
